import { Stack, Pivot, PivotItem } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import styles from "./AnalysisPanel.module.css";

import { SupportingContent } from "../SupportingContent";
import { ChatAppResponse } from "../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { ThoughtProcess } from "./ThoughtProcess";
import { MarkdownViewer } from "../MarkdownViewer";
import { useMsal } from "@azure/msal-react";
import { getHeaders } from "../../api";
import { useLogin, getToken } from "../../authConfig";
import { useState, useEffect } from "react";
import DOMPurify from "dompurify";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    onMetadataInfoReceived: (metadata: string) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
}

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

export const AnalysisPanel = ({ answer, activeTab, activeCitation, citationHeight, className, onActiveTabChanged, onMetadataInfoReceived }: Props) => {
    const isDisabledThoughtProcessTab: boolean = !answer.context.thoughts;
    const isDisabledSupportingContentTab: boolean = !answer.context.data_points;
    const isDisabledCitationTab: boolean = !activeCitation;
    const [citation, setCitation] = useState("");
    const [docxCit, setDocx] = useState("");
    const [showMetadata, setShowMetadata] = useState(false);

    const client = useLogin ? useMsal().instance : undefined;
    const { t } = useTranslation();

    const fetchCitation = async () => {
        setShowMetadata(false);
        const token = client ? await getToken(client) : undefined;
        let metadata_url = undefined;

        if (activeCitation) {
            const headers = await getHeaders(token);
            // Get hash from the URL as it may contain #page=N
            // which helps browser PDF renderer jump to correct page N
            const originalHash = activeCitation.indexOf("#") ? activeCitation.split("#")[1] : "";
            
            // Check if on mobile device
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            
            const response = await fetch(activeCitation, {
                method: "GET",
                headers: headers
            });
            metadata_url = response.headers.get("X-Metadata-url");

            if (isMobile) {
                // On mobile, use original server URL directly (blob URLs don't work in iframes on mobile)
                setCitation(activeCitation);
            } else {
                // On desktop, convert to blob URL
                const citationContent = await response.blob();
                let citationObjectUrl = URL.createObjectURL(citationContent);
                // Add hash back to the new blob URL
                if (originalHash) {
                    citationObjectUrl += "#" + originalHash;
                }
                setCitation(citationObjectUrl);
            }
            
            const fileExtension = activeCitation.split(".").pop()?.toLowerCase();
            if (fileExtension == "docx") {
                let response = await fetch("/getDocxRef" + activeCitation, {
                    method: "GET",
                    headers: headers
                });
                let res = await response.json();

                let clean = DOMPurify.sanitize(res.body, { USE_PROFILES: { html: true } });
                setDocx(clean);
            }
            if (metadata_url && metadata_url.indexOf("https://") == 0) {
                onMetadataInfoReceived(metadata_url);
                setShowMetadata(true);
            }
        }
    };
    useEffect(() => {
        fetchCitation();
    }, []);

    const renderFileViewer = () => {
        if (!activeCitation) {
            return null;
        }

        if (!citation) {
            return <div style={{ padding: "20px", textAlign: "center" }}>Loading<span className={styles.loadingdots} /></div>;
        }

        const fileExtension = activeCitation.split(".").pop()?.toLowerCase();
        switch (fileExtension) {
            case "png":
                return <img src={citation} className={styles.citationImg} alt="Citation Image" />;
            case "md":
                return <MarkdownViewer src={activeCitation} />;
            case "docx":
                return <div dangerouslySetInnerHTML={{ __html: docxCit }}></div>;
            default:
                return <iframe title="Citation" src={citation} width="100%" height={citationHeight} />;
        }
    };

    return (
        <Pivot
            className={className}
            selectedKey={activeTab}
            onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}
        >
            {/* <PivotItem
                itemKey={AnalysisPanelTabs.ThoughtProcessTab}
                headerText={t("headerTexts.thoughtProcess")}
                headerButtonProps={isDisabledThoughtProcessTab ? pivotItemDisabledStyle : undefined}
            >
                <ThoughtProcess thoughts={answer.context.thoughts || []} />
            </PivotItem>
            <PivotItem
                itemKey={AnalysisPanelTabs.SupportingContentTab}
                headerText={t("headerTexts.supportingContent")}
                headerButtonProps={isDisabledSupportingContentTab ? pivotItemDisabledStyle : undefined}
            >
                <SupportingContent supportingContent={answer.context.data_points} />
            </PivotItem> */}
            <PivotItem
                itemKey={AnalysisPanelTabs.CitationTab}
                headerText={t("headerTexts.citation")}
                headerButtonProps={isDisabledCitationTab ? pivotItemDisabledStyle : undefined}
            >
                {renderFileViewer()}
            </PivotItem>
            {showMetadata ? <PivotItem itemKey={AnalysisPanelTabs.OpenSharepointTab} headerText="Open in sharepoint" /> : null}
        </Pivot>
    );
};
